import {
  FormGroup,
  FormGroupFeedback,
  Icon16,
  IconNames16,
  IFormGroupProps,
  Intent,
  Spinner,
  Tag,
} from '@pinpointhq/thumbtack';
import { useField } from 'formik';
import { compact } from 'lodash';
import { contentType } from 'mime-types';
import * as React from 'react';
import { Attachment } from '../../../../../javascript/models';
import { directUpload, ErrorWithAdditionalData } from '../../../utilities/directUpload';
import { reportError } from '../../../utilities/reportError';

interface IFileFieldProps {
  accept: string[];
  disabled?: boolean;
  id?: string;
  name: string;
  formGroupProps?: Omit<IFormGroupProps, 'labelFor'>;
}

/**
 * NOTE: (TomLuce) This is a quickly written work in progress.
 * It does not cater for most of our file upload needs
 */
export function FileField({ accept, disabled, name, id, formGroupProps }: IFileFieldProps) {
  const [, meta, { setValue }] = useField<string>(name);
  const hasError = meta.touched && meta.error;

  const handleChange = React.useCallback(
    (file: IAttachedFile) => {
      if (disabled) return;

      if (file) {
        setValue(file.signedId);
      } else {
        setValue(null);
      }
    },
    [disabled, setValue],
  );

  /**
   * NOTE: (TomLuce) Don't use the labelFor prop on the FormGroup as we need that label to be used inside
   * the FileInput to open the file field
   */
  return (
    <FormGroup {...formGroupProps}>
      <SingleFileInput onChange={handleChange} id={id} {...{ accept, name, disabled }} />
      {hasError && <FormGroupFeedback intent={Intent.DANGER}>{meta.error}</FormGroupFeedback>}
    </FormGroup>
  );
}

interface ISingleFileInputProps {
  accept: string[];
  disabled?: boolean;
  id?: string;
  name?: string;
  onChange(file: IAttachedFile): void;
}

export function SingleFileInput({ accept, id, name, disabled, onChange }: ISingleFileInputProps) {
  const [file, setFile] = React.useState<IAttachedFile>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = React.useCallback(
    (event) => {
      if (disabled) return;

      const input = event.target;
      Array.from(input.files).map((inputFile) => uploadFile(inputFile));
    },
    [disabled],
  );

  function uploadFile(inputFile: File) {
    setIsLoading(true);

    directUpload(inputFile)
      .then((blob) => {
        const scaffolded = scaffoldFile(inputFile, blob.signed_id);
        setFile(scaffolded);
        onChange(scaffolded);
        setIsLoading(false);
      })
      .catch((e: Error | ErrorWithAdditionalData) => {
        // setError(e);

        if ('error' in e) {
          reportError(e.error, e.additionalData);
        } else {
          reportError(e);
        }

        setIsLoading(false);
      });
  }

  const acceptedFileTypes = compact(accept.map((fileType) => contentType(fileType))).join(',');

  async function handleDelete(attachedFile: IAttachedFile) {
    if (disabled) return;

    setIsLoading(true);
    if (attachedFile.id) {
      const attachmentToDestroy = new Attachment({ id: attachedFile.id, isPersisted: true });
      await attachmentToDestroy.destroy();
    }
    setIsLoading(false);
    setFile(null);
    onChange(null);
  }

  return (
    <div style={{ padding: '8px', border: '1px dashed #CCC', position: 'relative' }}>
      <input
        accept={acceptedFileTypes}
        disabled={disabled}
        onChange={handleInputChange}
        multiple={false}
        name={name}
        id={id || name}
        type="file"
        value={null}
        style={{ display: 'none' }}
      />
      <label
        className={`bp3-button bp3-button-style-tertiary ${disabled && 'bp3-disabled'}`}
        htmlFor={id || name}
        style={{ marginRight: '16px' }}
      >
        <Icon16 icon={IconNames16.UPLOAD} style={{ marginRight: '8px' }} />
        {file ? 'Replace' : 'Add'} File
      </label>
      {file && <FileTag key={[file.name, file.id].join('-')} handleRemove={handleDelete} {...{ file }} />}
      {isLoading && (
        <div style={{ position: 'absolute', right: '10px', top: '20px' }}>
          <Spinner small={true} />
        </div>
      )}
    </div>
  );
}

export interface IAttachedFile {
  name: string;
  path?: string;
  id?: string;
  signedId?: string;
}

const scaffoldFile = (file: File, signedId: string): IAttachedFile => ({
  id: null,
  name: file.name,
  path: null,
  signedId,
});

interface IFileTagProps {
  file: IAttachedFile;
  handleRemove(file: IAttachedFile): void;
}

function FileTag({ file, handleRemove }: IFileTagProps) {
  const onRemove = () => handleRemove(file);

  return (
    <Tag minimal={true} onRemove={onRemove} round={true} intent={Intent.SUCCESS}>
      {file.path ? (
        <a href={file.path} target="_blank" rel="noreferrer">
          {file.name}
        </a>
      ) : (
        file.name
      )}
    </Tag>
  );
}
